import { FC, lazy, Suspense } from "react";
import {
  BrowserRouter as Router,
  Navigate,
  Route,
  Routes,
  useLocation
} from "react-router-dom";

import { useAuthContext } from "providers/Auth";

import { Dashboard } from "shared/app/Dashboard/Dashboard";
import { NotFoundView } from "views/not-found";
import { Redirect } from "shared/components/Redirect";
import { VerificationTemplatesView } from "views/verification-templates";
import { VerificationTemplatesIdView } from "views/verification-templates-id";
import { ApplicationsView } from "views/applications";
import { ApplicationsIdReportView } from "views/applications-id-report";
import { ApplicationsIdOverview } from "views/applications-id";
import { AddPropertyPage } from "views/properties-add";
import { PropertyDetail } from "views/properties-id";
import { Properties } from "views/properties";
import { Units } from "views/units";
import { UnitDetail } from "views/properties-id-units-id";
import { AddUnitsView } from "views/properties-id-units-add";
import { RentReportingView } from "views/rent-reporting";
import { RentReportingUpload } from "views/rent-reporting-upload";
import { AddNewSubscriber } from "views/rent-reporting-add";
import { SubscriptionDetails } from "views/rent-reporting-id";
import { EditSubscriptionDetails } from "views/rent-reporting-id-edit";
import { RentersSettingsView } from "views/rent-reporting-settings";
import { Loader } from "shared/components/Loader";
import { RentReportingUploadIdView } from "views/rent-reporting-upload-id";
import { RentReportingCsvAnalysisView } from "views/rent-reporting-upload-csv-analysis";
import { ResubscribeSubscriptionDetails } from "views/rent-reporting-id-resubscribe";
import IntegrationsView from "views/settings-integration";
import { GoogleAnalyticsView } from "views/settings-integration/integrations/google-analytics";
import { PropertyGroups } from "views/property-groups";
import { PropertyGroupsConfiguration } from "views/property-groups-configuration";
import { PropertyGroupDetail } from "views/property-groups-id";
import { ApplicationsIdReportsPrintReceipt } from "views/applications-id-reports-print-receipt";
import { LoginWithSSO } from "views/login-with-sso";
import { BoomNotificationView } from "views/boom-notifications";
import { DiscountsRouteWrapper } from "providers/Routes/components/DiscountsRouteWrapper";
import { HubSpotView } from "views/settings-integration/integrations/hubspot";
import { CodeboxView } from "views/settings-integration/integrations/codebox";
import { ZillowView } from "views/settings-integration/integrations/zillow";

const RentersPage = lazy(() => import("views/renters"));
const ChatsView = lazy(() => import("views/chats"));
const OnboardingView = lazy(() => import("views/onboarding"));
const CreditImpactView = lazy(() => import("views/credit-impact"));
const AnalyticsView = lazy(() => import("views/analytics"));
const BillingView = lazy(() => import("views/billing"));
const BillingConfigurationView = lazy(
  () => import("views/settings-billing-configuration")
);
const StatementsIdView = lazy(() => import("views/statements-id"));
const BillingServicePeriodView = lazy(
  () => import("views/settings-billing-service-period")
);
const SignUpView = lazy(() => import("views/sign-up"));
const Login = lazy(() => import("views/login"));
const ForgotPassword = lazy(() => import("views/forgot-password"));
const SettingsView = lazy(() => import("views/settings"));
const Profile = lazy(() => import("views/settings-profile"));
const ApiView = lazy(() => import("views/settings-api"));
const PmsView = lazy(
  () => import("views/settings-integration/integrations/pms")
);
const UsersView = lazy(() => import("views/settings-users"));
const UserPermissionsView = lazy(() => import("views/settings-users-id"));
const RoleDetailView = lazy(() => import("views/settings-roles-id"));
const NotificationsView = lazy(() => import("views/settings-notifications"));
const FeaturesView = lazy(() => import("views/settings-features"));
const ApplicationView = lazy(() => import("views/settings-application"));
const TaskView = lazy(() => import("views/tasks"));
const SettingsCRMView = lazy(() => import("views/settings-crm"));
const DevDebugView = lazy(() => import("views/_dev-debug"));
const LeadsIdView = lazy(() => import("views/leads-id"));
const LeadsView = lazy(() => import("views/leads"));
const ContactsView = lazy(() => import("views/contacts"));
const ContactsIdView = lazy(() => import("views/contacts-id"));
const ForbiddenView = lazy(() => import("views/forbidden"));
const WorkflowsView = lazy(() => import("views/workflows"));
const WorkflowDetail = lazy(() => import("views/workflows-id"));
const ListingSettingsView = lazy(() => import("views/listing-settings"));
const ListingTemplatesIdView = lazy(() => import("views/listing-settings-id"));
const ShowingsView = lazy(() => import("views/showings"));
const LockboxesView = lazy(() => import("views/lockboxes"));

const DevATokenView = lazy(() => import("views/_dev-a-token"));
const SettingsShowingsPersonalView = lazy(
  () => import("views/settings-showings-personal")
);
const SettingsShowingView = lazy(() => import("views/settings-showings"));

const SettingsShowingsAccountView = lazy(
  () => import("views/settings-showings-account")
);

const RequireLoginRedirect = () => {
  const location = useLocation();
  return <Navigate to="/login" state={{ from: location }} replace />;
};

export const RouterProvider: FC = () => {
  const { auth, permissions, type } = useAuthContext();
  const { adminPrivileges } = type;
  if (auth === undefined)
    return (
      <div className="animate-appear pt-10 flex flex-col items-center">
        <Loader />
      </div>
    );

  return (
    <Router>
      <Routes>
        <Route
          path="/_dev/debug"
          element={
            <Suspense fallback={null}>
              <DevDebugView />
            </Suspense>
          }
        />
        <Route
          path="/_dev/a/:token"
          element={
            <Suspense fallback={null}>
              <DevATokenView />
            </Suspense>
          }
        />

        {!auth ? (
          <>
            <Route
              path="/forgot-password"
              element={
                <Suspense fallback={null}>
                  <ForgotPassword />
                </Suspense>
              }
            ></Route>
            <Route
              path="/sign-up"
              element={
                <Suspense fallback={null}>
                  <SignUpView />
                </Suspense>
              }
            ></Route>
            <Route
              path="/login"
              element={
                <Suspense fallback={null}>
                  <Login />
                </Suspense>
              }
            ></Route>
            <Route
              path="/login-with-sso"
              element={
                <Suspense fallback={null}>
                  <LoginWithSSO />
                </Suspense>
              }
            ></Route>
            <Route path="*" element={<RequireLoginRedirect />}></Route>
          </>
        ) : auth.partner.status === "active" ? (
          <>
            <Route path="/" element={<Dashboard />}>
              {type.isBoomScreen ? (
                <Route
                  path="chats"
                  element={
                    <Suspense fallback={null}>
                      <ChatsView />
                    </Suspense>
                  }
                />
              ) : null}
              {type.isBoomCRM ? (
                <>
                  <Route
                    path="leads"
                    element={
                      <Suspense fallback={null}>
                        <LeadsView />
                      </Suspense>
                    }
                  />
                  <Route
                    path="leads/:leadId"
                    element={
                      <Suspense fallback={null}>
                        <LeadsIdView />
                      </Suspense>
                    }
                  />
                  <Route
                    path="contacts"
                    element={
                      <Suspense fallback={null}>
                        <ContactsView />
                      </Suspense>
                    }
                  />
                  <Route
                    path="contacts/:contactId"
                    element={
                      <Suspense fallback={null}>
                        <ContactsIdView />
                      </Suspense>
                    }
                  />
                  <Route
                    path="tasks"
                    element={
                      <Suspense fallback={null}>
                        <TaskView />
                      </Suspense>
                    }
                  />
                  <Route
                    path="listing-settings"
                    element={
                      <Suspense fallback={null}>
                        <ListingSettingsView />
                      </Suspense>
                    }
                  />
                  <Route
                    path="listing-settings/:id"
                    element={
                      <Suspense fallback={null}>
                        <ListingTemplatesIdView />
                      </Suspense>
                    }
                  />
                  <Route
                    path="lockboxes"
                    element={
                      <Suspense fallback={null}>
                        <LockboxesView />
                      </Suspense>
                    }
                  />
                </>
              ) : null}
              {type.isBoomCRM && type.isScheduling ? (
                <Route
                  path="showings"
                  element={
                    <Suspense fallback={null}>
                      <ShowingsView />
                    </Suspense>
                  }
                />
              ) : null}
              <Route
                path="renters"
                element={
                  <Suspense fallback={null}>
                    <RentersPage />
                  </Suspense>
                }
              />
              <Route
                path="rent-reporting/settings"
                element={
                  <Suspense fallback={null}>
                    <RentersSettingsView />
                  </Suspense>
                }
              />
              <Route
                path="credit-impact"
                element={
                  <Suspense fallback={null}>
                    <CreditImpactView />
                  </Suspense>
                }
              />
              <Route path="rent-reporting" element={<RentReportingView />} />
              <Route
                path="rent-reporting/:renterId"
                element={<SubscriptionDetails />}
              />
              <Route
                path="rent-reporting/upload"
                element={<RentReportingUpload />}
              />
              <Route
                path="rent-reporting/upload/csv-analysis"
                element={<RentReportingCsvAnalysisView />}
              />
              <Route
                path="rent-reporting/upload/:id"
                element={<RentReportingUploadIdView />}
              />
              <Route path="rent-reporting/add" element={<AddNewSubscriber />} />
              <Route
                path="rent-reporting/:renterId/edit"
                element={<EditSubscriptionDetails />}
              />
              <Route
                path="rent-reporting/:renterId/resubscribe"
                element={<ResubscribeSubscriptionDetails />}
              />
              <Route path="notifications" element={<BoomNotificationView />} />

              <Route path="properties" element={<Properties />} />
              <Route path="applications" element={<ApplicationsView />} />
              <Route
                path="analytics"
                element={
                  <Suspense fallback={null}>
                    <AnalyticsView />
                  </Suspense>
                }
              />
              <Route
                path="applications/:applicationId"
                element={<ApplicationsIdOverview />}
              />
              {permissions.canViewReports ? (
                <Route
                  path="applications/:applicationId/reports"
                  element={<ApplicationsIdReportView />}
                />
              ) : null}
              <Route
                path="applications/:applicationId/reports/print/receipt"
                element={<ApplicationsIdReportsPrintReceipt />}
              />
              <Route path="properties/add" element={<AddPropertyPage />} />
              <Route
                path="properties/:propertyId"
                element={<PropertyDetail />}
              />
              <Route
                path="properties/:propertyId/units/add"
                element={<AddUnitsView />}
              />
              <Route path="units" element={<Units />} />
              <Route
                path="properties/:propertyId/units/:unitId"
                element={<UnitDetail />}
              />
              <Route path="property-groups" element={<PropertyGroups />} />
              <Route
                path="workflows"
                element={
                  <Suspense fallback={null}>
                    <WorkflowsView />
                  </Suspense>
                }
              />
              <Route
                path="workflows/:workflowId"
                element={
                  <Suspense fallback={null}>
                    <WorkflowDetail />
                  </Suspense>
                }
              />
              <Route
                path="property-groups/:id"
                element={<PropertyGroupDetail />}
              />
              <Route
                path="property-groups/configuration"
                element={<PropertyGroupsConfiguration />}
              />
              {permissions.canAccessScreeningTemplates ? (
                <>
                  <Route
                    path="verification-templates"
                    element={<VerificationTemplatesView />}
                  />
                  <Route
                    path="verification-templates/:id"
                    element={<VerificationTemplatesIdView />}
                  />
                </>
              ) : null}
              {permissions.canManageDiscounts ? (
                <Route path="discounts" element={<DiscountsRouteWrapper />} />
              ) : null}
              <Route
                path="forbidden"
                element={
                  <Suspense fallback={null}>
                    <ForbiddenView />
                  </Suspense>
                }
              />
              <Route path="*" element={<NotFoundView />} />
            </Route>
            <Route path="settings" element={<Dashboard />}>
              <Route
                index
                element={
                  <Suspense fallback={null}>
                    <SettingsView />
                  </Suspense>
                }
              />
              {adminPrivileges ? (
                <>
                  <Route
                    path="users"
                    element={
                      <Suspense fallback={null}>
                        <UsersView />
                      </Suspense>
                    }
                  />
                  <Route
                    path="users/:userId"
                    element={
                      <Suspense fallback={null}>
                        <UserPermissionsView />
                      </Suspense>
                    }
                  />
                  <Route
                    path="roles/:roleId"
                    element={
                      <Suspense fallback={null}>
                        <RoleDetailView />
                      </Suspense>
                    }
                  />
                  <Route
                    path="api"
                    element={
                      <Suspense fallback={null}>
                        <ApiView />
                      </Suspense>
                    }
                  />
                  <Route path="integration">
                    <Route
                      index
                      element={
                        <Suspense fallback={null}>
                          <IntegrationsView />
                        </Suspense>
                      }
                    />
                    <Route
                      path="pms"
                      element={
                        <Suspense fallback={null}>
                          <PmsView />
                        </Suspense>
                      }
                    />
                    <Route
                      path="google-analytics"
                      element={
                        <Suspense fallback={null}>
                          <GoogleAnalyticsView />
                        </Suspense>
                      }
                    />
                    <Route
                      path="hubspot"
                      element={
                        <Suspense fallback={null}>
                          <HubSpotView />
                        </Suspense>
                      }
                    />
                    <Route
                      path="codebox"
                      element={
                        <Suspense fallback={null}>
                          <CodeboxView />
                        </Suspense>
                      }
                    />
                    <Route
                      path="zillow"
                      element={
                        <Suspense fallback={null}>
                          <ZillowView />
                        </Suspense>
                      }
                    />
                  </Route>

                  <Route
                    path="application"
                    element={
                      <Suspense fallback={null}>
                        <ApplicationView />
                      </Suspense>
                    }
                  />
                  <Route
                    path="billing"
                    element={
                      <Suspense fallback={null}>
                        <BillingView />
                      </Suspense>
                    }
                  />
                  <Route
                    path="billing/configuration"
                    element={
                      <Suspense fallback={null}>
                        <BillingConfigurationView />
                      </Suspense>
                    }
                  />
                  <Route
                    path="billing/statements/:statementId"
                    element={
                      <Suspense fallback={null}>
                        <StatementsIdView />
                      </Suspense>
                    }
                  />
                  <Route
                    path="billing/service-periods/:servicePeriodDate"
                    element={
                      <Suspense fallback={null}>
                        <BillingServicePeriodView />
                      </Suspense>
                    }
                  />
                  <Route
                    path="billing/service-periods/:servicePeriodDate/statements/:statementId"
                    element={
                      <Suspense fallback={null}>
                        <StatementsIdView />
                      </Suspense>
                    }
                  />
                  <Route
                    path="features"
                    element={
                      <Suspense fallback={null}>
                        <FeaturesView />
                      </Suspense>
                    }
                  />
                </>
              ) : null}
              <Route
                path="profile"
                element={
                  <Suspense fallback={null}>
                    <Profile />
                  </Suspense>
                }
              />
              {type.isBoomCRM && type.isScheduling ? (
                <>
                  <Route
                    path="showings"
                    element={
                      <Suspense fallback={null}>
                        <SettingsShowingView />
                      </Suspense>
                    }
                  />
                  <Route
                    path="showings/account"
                    element={
                      <Suspense fallback={null}>
                        <SettingsShowingsAccountView />
                      </Suspense>
                    }
                  />
                  <Route
                    path="showings/personal"
                    element={
                      <Suspense fallback={null}>
                        <SettingsShowingsPersonalView />
                      </Suspense>
                    }
                  />
                </>
              ) : null}
              <Route
                path="notifications"
                element={
                  <Suspense fallback={null}>
                    <NotificationsView />
                  </Suspense>
                }
              />
              {type.isBoomCRM ? (
                <Route
                  path="crm"
                  element={
                    <Suspense fallback={null}>
                      <SettingsCRMView />
                    </Suspense>
                  }
                />
              ) : null}
            </Route>

            {/* REDIRECTS */}
            <Route path="/login" element={<Navigate to="/" />} />
            <Route path="/forgot-password" element={<Navigate to="/" />} />
            <Route path="/sign-up" element={<Navigate to="/" />} />
            <Route path="/onboarding" element={<Navigate to="/" />} />
          </>
        ) : (
          <>
            <Route
              path="/onboarding"
              element={
                <Suspense fallback={null}>
                  <OnboardingView />
                </Suspense>
              }
            />
            <Route path="*" element={<Redirect href="/onboarding" />} />
          </>
        )}
      </Routes>
    </Router>
  );
};
